/**
 * Sprint 33 (GT-TENANT-ADMIN-COMPLETE-02) — pure helpers for the
 * lifecycle date dialog. Mirrors the backend zod contract in
 * `services/api/src/modules/admin/tenant-lifecycle.routes.ts`:
 *
 *   POST /v1/admin/tenants/:id/lifecycle/mark-trial
 *     body: { trialUntil: ISO datetime string }
 *
 *   POST /v1/admin/tenants/:id/lifecycle/set-valid-until
 *     body: { validUntil: ISO datetime string | null, setActive?: boolean }
 *
 * The drawer accepts an HTML `<input type="datetime-local">` value
 * (e.g. `"2026-12-31T23:59"`) and these helpers normalise it to the
 * ISO offset / Z form the backend zod schema expects.
 */

export type LifecycleDateAction = "mark-trial" | "set-valid-until";

export interface LifecycleDatesDraft {
  /** Local datetime string from `<input type="datetime-local">`. */
  readonly localValue: string;
  /** Only meaningful for `set-valid-until`; backend ignores it for mark-trial. */
  readonly setActive: boolean;
  /** Only meaningful for `set-valid-until`; sends `validUntil: null` when true. */
  readonly clear: boolean;
}

export interface LifecycleDatesValidation {
  readonly localValue?: "required" | "invalid";
}

export interface MarkTrialBody {
  readonly trialUntil: string;
}

export interface SetValidUntilBody {
  readonly validUntil: string | null;
  readonly setActive?: boolean;
}

export type LifecycleDatesBody = MarkTrialBody | SetValidUntilBody;

export function emptyDraft(): LifecycleDatesDraft {
  return { localValue: "", setActive: false, clear: false };
}

export function validateDraft(
  draft: LifecycleDatesDraft,
  action: LifecycleDateAction,
): LifecycleDatesValidation {
  // `mark-trial` requires a date. `set-valid-until` requires a date
  // unless `clear` is true.
  if (action === "set-valid-until" && draft.clear) {
    return {};
  }
  if (draft.localValue.trim().length === 0) {
    return { localValue: "required" };
  }
  const parsed = new Date(draft.localValue);
  if (Number.isNaN(parsed.getTime())) {
    return { localValue: "invalid" };
  }
  return {};
}

export function isValid(v: LifecycleDatesValidation): boolean {
  return Object.keys(v).length === 0;
}

/**
 * Build the body for the relevant lifecycle endpoint. Returns null
 * when the draft is invalid OR is a no-op (e.g. set-valid-until
 * with the same value as the current snapshot).
 *
 * `currentIso` is the ISO string already on the tenant (e.g.
 * `tenant.trialUntil` or `tenant.validUntil`); when the operator's
 * proposed value equals it the helper returns null so the drawer
 * keeps Save disabled.
 */
export function buildBody(
  draft: LifecycleDatesDraft,
  action: LifecycleDateAction,
  currentIso: string | null,
): LifecycleDatesBody | null {
  if (!isValid(validateDraft(draft, action))) return null;

  if (action === "set-valid-until" && draft.clear) {
    if (currentIso === null) return null;
    return { validUntil: null };
  }

  const proposedIso = new Date(draft.localValue).toISOString();
  if (proposedIso === currentIso) return null;

  if (action === "mark-trial") {
    return { trialUntil: proposedIso };
  }
  // set-valid-until
  const body: SetValidUntilBody = { validUntil: proposedIso };
  if (draft.setActive) {
    return { ...body, setActive: true };
  }
  return body;
}

/**
 * Convert an ISO datetime string from the API to a value the
 * `<input type="datetime-local">` element accepts (`YYYY-MM-DDTHH:mm`).
 * Returns the empty string when the input is null/undefined or
 * unparsable so the input renders empty.
 */
export function isoToLocalInputValue(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  // Format as YYYY-MM-DDTHH:mm in LOCAL time (the value the
  // datetime-local input expects). pad to two digits.
  const pad = (n: number) => String(n).padStart(2, "0");
  const yyyy = d.getFullYear();
  const mm = pad(d.getMonth() + 1);
  const dd = pad(d.getDate());
  const hh = pad(d.getHours());
  const min = pad(d.getMinutes());
  return `${yyyy}-${mm}-${dd}T${hh}:${min}`;
}
